import { image } from './images';
import { CANVAS_WIDTH, type Slot, type StudioLayout } from './layouts';

/**
 * Paints a scene onto the broadcast canvas.
 *
 * ## One function, called every frame
 *
 * No state of its own, no React, no reads of anything but the scene it is
 * handed. That is what lets the same code paint the preview the host is looking
 * at and the canvas being captured for the stream, at two different sizes, with
 * no second implementation to keep in step - the preview *is* the broadcast,
 * which is the only way a preview is worth having.
 *
 * ## It never throws on missing pieces
 *
 * A camera that has not started, artwork still decoding, a room with nothing
 * playing: all of these are normal states during a broadcast, not errors. Each
 * one just does not get drawn. The alternative - a render loop that can throw -
 * is a black stream every time somebody's webcam takes an extra second.
 */

export interface Scene {
  layout: StudioLayout;
  /** Dark-to-light stops, as the profile header uses. */
  background: string[];
  backgroundAngle: number;

  camera: HTMLVideoElement | null;
  showCamera: boolean;

  track: {
    title: string;
    artist: string;
    artwork: string | null;
  } | null;
  showCard: boolean;

  brand: {
    username: string;
    displayName: string;
    avatar: string | null;
  } | null;
  showBrand: boolean;

  /** Drawn over everything when the room is on air. */
  live: boolean;
}

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

const SANS =
  "'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
const MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

const INK = '#f4f1ea';
const SOFT = 'rgba(244, 241, 234, 0.66)';
const HOT = '#ff3b30';

export function paint(
  ctx: CanvasRenderingContext2D,
  scene: Scene,
  width: number,
  height: number,
): void {
  // Layout slots are fractions, so a scene composed for the preview and one
  // composed for the encoder differ only by this conversion.
  const px = (slot: Slot): Box => ({
    x: slot.x * width,
    y: slot.y * height,
    w: slot.w * width,
    h: slot.h * height,
  });

  // The scale for anything measured in design pixels - text sizes, padding,
  // radii - so the preview is a smaller copy rather than the same 40px type on
  // a quarter-size frame.
  const k = width / CANVAS_WIDTH;

  ctx.save();
  ctx.clearRect(0, 0, width, height);

  paintBackground(ctx, scene, width, height);

  const { layout } = scene;

  if (layout.camera && scene.showCamera && ready(scene.camera)) {
    const box = px(layout.camera);

    if (layout.cameraShape === 'circle') {
      drawCircleVideo(ctx, scene.camera as HTMLVideoElement, box, k);
    } else {
      drawRoundedVideo(ctx, scene.camera as HTMLVideoElement, box, layout.id === 'camera' ? 0 : 18 * k);
    }
  }

  if (layout.card && scene.showCard && scene.track) {
    const box = px(layout.card);

    if (layout.cardStyle === 'bar') drawBar(ctx, scene.track, box, k);
    if (layout.cardStyle === 'panel') drawPanel(ctx, scene.track, box, k);
    if (layout.cardStyle === 'hero') drawHero(ctx, scene.track, box, k);
  }

  if (layout.brand && scene.showBrand && scene.brand) {
    drawBrand(ctx, scene.brand, px(layout.brand), k);
  }

  if (scene.live) drawLiveBadge(ctx, width, k);

  ctx.restore();
}

/* -------------------------------------------------------------------------- */
/* Background                                                                  */
/* -------------------------------------------------------------------------- */

function paintBackground(
  ctx: CanvasRenderingContext2D,
  scene: Scene,
  width: number,
  height: number,
): void {
  // Painted even under a full-bleed camera. A camera that has not started yet,
  // or one the host switched off, would otherwise leave the canvas transparent -
  // which encodes as black on some paths and as the previous frame on others.
  const angle = ((scene.backgroundAngle - 90) * Math.PI) / 180;
  const reach = Math.max(width, height);
  const gradient = ctx.createLinearGradient(
    width / 2 - (Math.cos(angle) * reach) / 2,
    height / 2 - (Math.sin(angle) * reach) / 2,
    width / 2 + (Math.cos(angle) * reach) / 2,
    height / 2 + (Math.sin(angle) * reach) / 2,
  );

  const stops = scene.background.length > 0 ? scene.background : ['#0d0d10', '#1a1a20'];

  stops.forEach((colour, i) => {
    gradient.addColorStop(stops.length === 1 ? 0 : i / (stops.length - 1), colour);
  });

  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);
}

/* -------------------------------------------------------------------------- */
/* Camera                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * A video element with a frame in it.
 *
 * `readyState` alone is not enough: an element can report metadata before the
 * first frame exists, and `drawImage` of a zero-width video throws
 * `InvalidStateError` - on every frame, during startup, which is exactly when
 * the host is watching the preview to decide whether this works at all.
 */
function ready(video: HTMLVideoElement | null): video is HTMLVideoElement {
  return video !== null && video.readyState >= 2 && video.videoWidth > 0;
}

/** The source rectangle that fills a box without distorting the picture. */
function coverSource(video: HTMLVideoElement, box: { w: number; h: number }) {
  const scale = Math.max(box.w / video.videoWidth, box.h / video.videoHeight);
  const w = box.w / scale;
  const h = box.h / scale;

  return {
    sx: (video.videoWidth - w) / 2,
    sy: (video.videoHeight - h) / 2,
    sw: w,
    sh: h,
  };
}

function drawRoundedVideo(
  ctx: CanvasRenderingContext2D,
  video: HTMLVideoElement,
  box: Box,
  radius: number,
): void {
  const { sx, sy, sw, sh } = coverSource(video, box);

  ctx.save();

  if (radius > 0) {
    ctx.beginPath();
    ctx.roundRect(box.x, box.y, box.w, box.h, radius);
    ctx.clip();
  }

  ctx.drawImage(video, sx, sy, sw, sh, box.x, box.y, box.w, box.h);
  ctx.restore();
}

function drawCircleVideo(
  ctx: CanvasRenderingContext2D,
  video: HTMLVideoElement,
  box: Box,
  k: number,
): void {
  // Inscribed in the slot's shorter side, so a slot that is not square still
  // yields a circle rather than an ellipse.
  const d = Math.min(box.w, box.h);
  const x = box.x + (box.w - d) / 2;
  const y = box.y + (box.h - d) / 2;
  const { sx, sy, sw, sh } = coverSource(video, { w: d, h: d });

  ctx.save();
  ctx.beginPath();
  ctx.arc(x + d / 2, y + d / 2, d / 2, 0, Math.PI * 2);
  ctx.clip();
  ctx.drawImage(video, sx, sy, sw, sh, x, y, d, d);
  ctx.restore();

  // A ring, because a circular camera over a dark background has no edge and
  // reads as a hole rather than as an inset.
  const line = 3 * k;

  ctx.save();
  ctx.strokeStyle = 'rgba(244, 241, 234, 0.28)';
  ctx.lineWidth = line;
  ctx.beginPath();
  ctx.arc(x + d / 2, y + d / 2, d / 2 - line / 2, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

/* -------------------------------------------------------------------------- */
/* Now playing                                                                 */
/* -------------------------------------------------------------------------- */

type Track = NonNullable<Scene['track']>;

/** A lower third: artwork, title, artist, over a scrim. */
function drawBar(ctx: CanvasRenderingContext2D, track: Track, box: Box, k: number): void {
  ctx.save();
  ctx.beginPath();
  ctx.roundRect(box.x, box.y, box.w, box.h, 14 * k);
  // Dark and translucent rather than opaque: it sits over a face, and a solid
  // block reads as a fault in the encoder.
  ctx.fillStyle = 'rgba(8, 8, 11, 0.72)';
  ctx.fill();
  ctx.clip();

  const pad = box.h * 0.14;
  const size = box.h - pad * 2;
  const artwork = image(track.artwork);

  if (artwork) {
    ctx.save();
    ctx.beginPath();
    ctx.roundRect(box.x + pad, box.y + pad, size, size, 6 * k);
    ctx.clip();
    ctx.drawImage(artwork, box.x + pad, box.y + pad, size, size);
    ctx.restore();
  }

  const textX = box.x + pad + (artwork ? size + pad : 0);
  const textW = box.x + box.w - pad - textX;

  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = INK;
  ctx.font = `700 ${Math.round(30 * k)}px ${SANS}`;
  ctx.fillText(clip(ctx, track.title, textW), textX, box.y + box.h * 0.48);

  ctx.fillStyle = SOFT;
  ctx.font = `500 ${Math.round(21 * k)}px ${SANS}`;
  ctx.fillText(clip(ctx, track.artist, textW), textX, box.y + box.h * 0.76);

  ctx.restore();
}

/** A column beside the camera: large artwork above the text. */
function drawPanel(ctx: CanvasRenderingContext2D, track: Track, box: Box, k: number): void {
  ctx.save();
  ctx.beginPath();
  ctx.roundRect(box.x, box.y, box.w, box.h, 18 * k);
  ctx.fillStyle = 'rgba(8, 8, 11, 0.55)';
  ctx.fill();
  ctx.clip();

  const pad = box.w * 0.09;
  const size = box.w - pad * 2;
  const artwork = image(track.artwork);

  if (artwork) {
    ctx.save();
    ctx.beginPath();
    ctx.roundRect(box.x + pad, box.y + pad, size, size, 10 * k);
    ctx.clip();
    ctx.drawImage(artwork, box.x + pad, box.y + pad, size, size);
    ctx.restore();
  }

  const top = box.y + pad + (artwork ? size + pad * 1.1 : 0);

  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = SOFT;
  ctx.font = `600 ${Math.round(15 * k)}px ${MONO}`;
  ctx.fillText('NOW PLAYING', box.x + pad, top);

  ctx.fillStyle = INK;
  ctx.font = `700 ${Math.round(30 * k)}px ${SANS}`;
  const after = wrap(ctx, track.title, box.x + pad, top + 42 * k, size, 36 * k, 2);

  ctx.fillStyle = SOFT;
  ctx.font = `500 ${Math.round(22 * k)}px ${SANS}`;
  ctx.fillText(clip(ctx, track.artist, size), box.x + pad, after + 34 * k);

  ctx.restore();
}

/** The artwork as the subject, centred, with the text beneath it. */
function drawHero(ctx: CanvasRenderingContext2D, track: Track, box: Box, k: number): void {
  const artwork = image(track.artwork);

  // Room reserved for two lines of title and one of artist whether or not the
  // artwork loaded, so a late-decoding cover does not shove the text down in
  // the middle of a broadcast.
  const reserved = 150 * k;
  const size = Math.min(box.w, box.h - reserved);
  const ax = box.x + (box.w - size) / 2;
  const ay = box.y;

  if (artwork) {
    ctx.save();
    ctx.shadowColor = 'rgba(0, 0, 0, 0.55)';
    ctx.shadowBlur = 40 * k;
    ctx.shadowOffsetY = 12 * k;
    ctx.beginPath();
    ctx.roundRect(ax, ay, size, size, 16 * k);
    ctx.fillStyle = '#0d0d10';
    ctx.fill();
    ctx.restore();

    ctx.save();
    ctx.beginPath();
    ctx.roundRect(ax, ay, size, size, 16 * k);
    ctx.clip();
    ctx.drawImage(artwork, ax, ay, size, size);
    ctx.restore();
  }

  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';

  const cx = box.x + box.w / 2;

  ctx.fillStyle = INK;
  ctx.font = `700 ${Math.round(40 * k)}px ${SANS}`;
  const after = wrap(ctx, track.title, cx, ay + size + 52 * k, box.w, 46 * k, 2);

  ctx.fillStyle = SOFT;
  ctx.font = `500 ${Math.round(26 * k)}px ${SANS}`;
  ctx.fillText(clip(ctx, track.artist, box.w), cx, after + 40 * k);
  ctx.restore();
}

/* -------------------------------------------------------------------------- */
/* Brand and status                                                            */
/* -------------------------------------------------------------------------- */

function drawBrand(
  ctx: CanvasRenderingContext2D,
  brand: NonNullable<Scene['brand']>,
  box: Box,
  k: number,
): void {
  const d = box.h;
  const avatar = image(brand.avatar);

  if (avatar) {
    ctx.save();
    ctx.beginPath();
    ctx.arc(box.x + d / 2, box.y + d / 2, d / 2, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(avatar, box.x, box.y, d, d);
    ctx.restore();
  }

  const textX = box.x + (avatar ? d + 14 * k : 0);

  ctx.save();
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = INK;
  ctx.font = `700 ${Math.round(22 * k)}px ${SANS}`;
  ctx.fillText(clip(ctx, brand.displayName, box.w - (textX - box.x)), textX, box.y + d * 0.46);

  ctx.fillStyle = SOFT;
  ctx.font = `500 ${Math.round(16 * k)}px ${MONO}`;
  ctx.fillText(`/${brand.username}`, textX, box.y + d * 0.86);
  ctx.restore();
}

function drawLiveBadge(ctx: CanvasRenderingContext2D, width: number, k: number): void {
  const pad = 28 * k;
  const h = 34 * k;
  const w = 96 * k;
  const x = width - pad - w;

  ctx.save();
  ctx.beginPath();
  ctx.roundRect(x, pad, w, h, h / 2);
  ctx.fillStyle = 'rgba(255, 59, 48, 0.16)';
  ctx.fill();
  ctx.strokeStyle = 'rgba(255, 59, 48, 0.5)';
  ctx.lineWidth = 1.5 * k;
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(x + h * 0.5, pad + h / 2, 4.5 * k, 0, Math.PI * 2);
  ctx.fillStyle = HOT;
  ctx.fill();

  ctx.fillStyle = HOT;
  ctx.font = `700 ${Math.round(15 * k)}px ${MONO}`;
  ctx.textBaseline = 'middle';
  ctx.fillText('LIVE', x + h * 0.5 + 12 * k, pad + h / 2 + 1);
  ctx.restore();
}

/* -------------------------------------------------------------------------- */
/* Text                                                                        */
/* -------------------------------------------------------------------------- */

/** One line, ellipsised to fit. */
function clip(ctx: CanvasRenderingContext2D, text: string, max: number): string {
  if (ctx.measureText(text).width <= max) return text;

  let out = text;

  while (out.length > 1 && ctx.measureText(`${out}…`).width > max) {
    out = out.slice(0, -1);
  }

  return `${out}…`;
}

/**
 * Word-wrapped text, capped at `lines`.
 *
 * Returns the baseline it finished on, so whatever comes next sits correctly
 * under a title of either length rather than at a fixed offset that only looks
 * right for one of them.
 */
function wrap(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  max: number,
  lineHeight: number,
  lines: number,
): number {
  const rows: string[] = [];
  let row = '';

  for (const word of text.split(' ')) {
    const next = row ? `${row} ${word}` : word;

    if (ctx.measureText(next).width > max && row) {
      rows.push(row);
      row = word;

      if (rows.length === lines) {
        row = '';
        break;
      }
    } else {
      row = next;
    }
  }

  if (rows.length < lines && row) rows.push(row);

  let baseline = y;

  rows.forEach((line, i) => {
    baseline = y + i * lineHeight;
    // Every row is clipped, not just the last: a single word longer than the
    // slot never wraps and would otherwise run out of the card.
    ctx.fillText(clip(ctx, line, max), x, baseline);
  });

  return baseline;
}
